import React, { useState } from 'react';
import { Modal, Tabs, Input, Typography } from 'antd';
import { SearchOutlined, UserOutlined, TeamOutlined, ShopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ResidentListTab, EmployeeListTab, VendorListTab } from '@/components/tab';
import { Resident } from '@/types/resources/resident';
import { Employee } from '@/types/resources/employee';
import { Vendor } from '@/types/resources/vendor';

const { Title } = Typography;

interface StartConversationModalProps {
  visible: boolean;
  onCancel: () => void;
  onSelectResident: (resident: Resident) => void;
  onSelectEmployee: (employee: Employee) => void;
  onSelectVendor: (vendor: Vendor) => void;
}

export const StartConversationModal: React.FC<StartConversationModalProps> = ({
  visible,
  onCancel,
  onSelectResident,
  onSelectEmployee,
  onSelectVendor
}) => {
  const { t } = useTranslation('messages');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('residents');

  const handleSelectResident = (resident: Resident) => {
    onSelectResident(resident);
    onCancel();
  };

  const handleSelectEmployee = (employee: Employee) => {
    onSelectEmployee(employee);
    onCancel();
  };

  const handleSelectVendor = (vendor: Vendor) => {
    onSelectVendor(vendor);
    onCancel();
  };

  const handleCancel = () => {
    setSearchTerm('');
    setActiveTab('residents');
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UserOutlined style={{ color: '#1890ff' }} />
          <Title level={4} style={{ margin: 0 }}>
            {t('startNewConversation', 'Start New Conversation')}
          </Title>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder={t('searchPeople', 'Search people...')}
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          size="large"
        />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'residents',
            label: (
              <span>
                <UserOutlined style={{ marginRight: 8 }} />
                {t('residents', 'Residents')}
              </span>
            ),
            children: (
              <ResidentListTab
                onSelect={handleSelectResident}
                searchTerm={searchTerm}
              />
            ),
          },
          {
            key: 'employees',
            label: (
              <span>
                <TeamOutlined style={{ marginRight: 8 }} />
                {t('employees', 'Employees')}
              </span>
            ),
            children: (
              <EmployeeListTab
                onSelect={handleSelectEmployee}
                searchTerm={searchTerm}
              />
            ),
          },
          {
            key: 'vendors',
            label: (
              <span>
                <ShopOutlined style={{ marginRight: 8 }} />
                {t('vendors', 'Vendors')}
              </span>
            ),
            children: (
              <VendorListTab
                onSelect={handleSelectVendor}
                searchTerm={searchTerm}
              />
            ),
          },
        ]}
      />
    </Modal>
  );
};
